"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Subscription, Vehicle } from "@/types";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { generateTimeSlots } from "@/lib/utils";

const weekDays = [
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
  { label: "Sunday", value: "SUNDAY" },
];

// Generate time slots with 15-minute intervals
const timeSlots = generateTimeSlots();

// 8-wash plans that have 2 scheduling rules
const TWO_RULE_PLANS = ["PLATINUM", "GOLD", "SILVER LITE"];

interface EditSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  vehicles: Vehicle[];
  onRefreshPaginated?: () => Promise<void>;
}

export function EditSubscriptionDialog({
  open,
  onOpenChange,
  subscription,
  vehicles,
  onRefreshPaginated,
}: EditSubscriptionDialogProps) {
  const { updateUserSubscriptionDetails, refreshSubscriptions } =
    useSubscriptions();

  const [formData, setFormData] = useState({
    day: "",
    time: "",
    day2: "",
    time2: "",
    vehicleId: "",
    isActive: false,
  });

  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [initialIsActive, setInitialIsActive] = useState(false);
  const [hasSecondRule, setHasSecondRule] = useState(false);

  // Check if this subscription's plan requires 2 rules
  const isTwoRulePlan = useMemo(() => {
    if (!subscription) return false;
    const planName =
      subscription.planName?.replace(/\s+/g, " ").trim().toUpperCase() || "";
    return TWO_RULE_PLANS.includes(planName);
  }, [subscription]);

  // Filter vehicles by customer
  const customerVehicles = useMemo(() => {
    if (!subscription) return [];
    return vehicles.filter((v) => v.customerId === subscription.customerId);
  }, [vehicles, subscription]);

  // Load subscription data when dialog opens
  useEffect(() => {
    if (open && subscription) {
      const loadSubscriptionData = async () => {
        try {
          setLoadingDetails(true);
          // Fetch full subscription details to get rule IDs
          const fullSubscription =
            await userSubscriptionService.getUserSubscriptionById(
              subscription.id,
            );

          const firstRule =
            fullSubscription.rules && fullSubscription.rules.length > 0
              ? fullSubscription.rules[0]
              : null;
          const secondRule =
            fullSubscription.rules && fullSubscription.rules.length > 1
              ? fullSubscription.rules[1]
              : null;

          setHasSecondRule(!!secondRule);

          setFormData({
            day:
              firstRule?.day?.toUpperCase() ||
              subscription.recurringDay.toUpperCase() ||
              "MONDAY",
            time: firstRule?.time || subscription.preferredTime || "10:00 AM",
            day2:
              secondRule?.day?.toUpperCase() ||
              subscription.recurringDay2?.toUpperCase() ||
              "WEDNESDAY",
            time2:
              secondRule?.time || subscription.preferredTime2 || "10:00 AM",
            vehicleId:
              firstRule?.vehicleId ||
              subscription.vehicleId ||
              customerVehicles[0]?.id ||
              "",
            isActive: fullSubscription.isActive,
          });
          setInitialIsActive(fullSubscription.isActive);
        } catch (error) {
          console.error("Error loading subscription data:", error);
          // Fallback to subscription props
          setFormData({
            day: subscription.recurringDay.toUpperCase() || "MONDAY",
            time: subscription.preferredTime || "10:00 AM",
            day2: subscription.recurringDay2?.toUpperCase() || "WEDNESDAY",
            time2: subscription.preferredTime2 || "10:00 AM",
            vehicleId: subscription.vehicleId || customerVehicles[0]?.id || "",
            isActive: subscription.isActive ?? false,
          });
          setInitialIsActive(subscription.isActive ?? false);
        } finally {
          setLoadingDetails(false);
        }
      };

      loadSubscriptionData();
    }
  }, [open, subscription, customerVehicles]);

  const handleSaveDetails = async () => {
    if (!subscription) return;

    // Validate
    if (!formData.day || !formData.time) {
      return;
    }

    try {
      setLoading(true);
      await updateUserSubscriptionDetails(subscription.id, {
        day: formData.day,
        time: formData.time,
        vehicleId: formData.vehicleId || undefined,
      });
      if (onRefreshPaginated) {
        await onRefreshPaginated();
      }
      onOpenChange(false);
    } catch (error) {
      // Error is handled in context
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIsActive = async (newIsActive: boolean) => {
    if (!subscription) return;

    let statusUpdated = false;

    try {
      setLoading(true);
      if (newIsActive) {
        await userSubscriptionService.reactivateUserSubscription(subscription.id);
      } else {
        await userSubscriptionService.expireUserSubscription(subscription.id);
      }
      statusUpdated = true;

      setFormData((prev) => ({
        ...prev,
        isActive: newIsActive,
      }));
      setInitialIsActive(newIsActive);
      await refreshSubscriptions();
      if (onRefreshPaginated) {
        await onRefreshPaginated();
      }
      onOpenChange(false);
    } catch (error) {
      // Revert toggle only if the status update request failed.
      if (!statusUpdated) {
        setFormData((prev) => ({ ...prev, isActive: initialIsActive }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Subscription</DialogTitle>
          <DialogDescription>
            Update subscription details and status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Active Status Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="isActive" className="text-base">
                Subscription Status
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.isActive
                  ? "Subscription is active"
                  : "Subscription is expired"}
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={handleToggleIsActive}
              disabled={loading}
            />
          </div>

          {/* Schedule 1 - Day Selection */}
          <div className="grid gap-2">
            <Label htmlFor="day">
              {isTwoRulePlan || hasSecondRule
                ? "Schedule 1 - Day *"
                : "Recurring Day *"}
            </Label>
            <Select
              value={formData.day}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, day: value }))
              }
              disabled={loading || loadingDetails}
            >
              <SelectTrigger id="day">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                {weekDays.map((day) => (
                  <SelectItem key={day.value} value={day.value}>
                    {day.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule 1 - Time Selection */}
          <div className="grid gap-2">
            <Label htmlFor="time">
              {isTwoRulePlan || hasSecondRule
                ? "Schedule 1 - Time *"
                : "Preferred Time *"}
            </Label>
            <Select
              value={formData.time}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, time: value }))
              }
              disabled={loading || loadingDetails}
            >
              <SelectTrigger id="time">
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.map((slot) => (
                  <SelectItem key={slot.value} value={slot.label}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Schedule 2 — Only shown for 2-rule plans or if subscription already has 2 rules */}
          {(isTwoRulePlan || hasSecondRule) && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="day2">Schedule 2 - Day *</Label>
                <Select
                  value={formData.day2}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, day2: value }))
                  }
                  disabled={loading || loadingDetails}
                >
                  <SelectTrigger id="day2">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    {weekDays.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="time2">Schedule 2 - Time *</Label>
                <Select
                  value={formData.time2}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, time2: value }))
                  }
                  disabled={loading || loadingDetails}
                >
                  <SelectTrigger id="time2">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.label}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Vehicle Selection */}
          <div className="grid gap-2">
            <Label htmlFor="vehicleId">Vehicle</Label>
            {customerVehicles.length === 0 ? (
              <div className="text-sm text-muted-foreground p-2 border rounded-md">
                No vehicles available for this customer
              </div>
            ) : (
              <Select
                value={formData.vehicleId}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, vehicleId: value }))
                }
                disabled={loading || loadingDetails}
              >
                <SelectTrigger id="vehicleId">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {customerVehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDetails}
            disabled={
              loading || loadingDetails || !formData.day || !formData.time
            }
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
